import React, { memo, useEffect, useRef, useState } from "react";
import { classNames } from "helpers";
import { VehicleSummaryCardProps } from "./types";
import { useTranslate } from "helpers";
import Logo from "ui/modules/public/components/Logo/Logo";
import Lottie, { LottieRefCurrentProps } from "lottie-react";
import successAnimation from "ui/theme/animations/success.json";

import VehiclePickerSummary from "./VehiclePicker/VehiclePickerSummary";
import VehicleInfoSummary from "./VehicleInfo/VehicleInfoSummary";
import VehicleEquipmentSummary from "./VehicleEquipment/VehicleEquipmentSummary";
import VehicleTireSummary from "./VehicleTire/VehicleTireSummary";
import VehicleFireExtinguisherSummary from "./VehicleFireExtinguisher/VehicleFireExtinguisherSummary";
import VehicleOwnerSummary from "./VehicleOwner/VehicleOwnerSummary";
import VehiclePriceSummary from "./VehiclePrice/VehiclePriceSummary";
import VehiclePictureSummary from "./VehiclePicture/VehiclePictureSummary";
import VehicleDocumentationSummary from "./VehicleDocumentation/VehicleDocumentationSummary";
import VehicleIntroSummary from "./VehicleIntro/VehicleIntroSummary";

const VehicleSummaryCard: React.FC<VehicleSummaryCardProps> = ({
  vehicleInfo,
  stepCurrent,
  isFinished = false,
}) => {
  const { t } = useTranslate();
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const [showFinish, setShowFinish] = useState(false);

  /** Pokreni završnu animaciju kad se vozilo uspješno objavi */
  useEffect(() => {
    if (isFinished) {
      const timer = setTimeout(() => setShowFinish(true), 50);
      return () => clearTimeout(timer);
    }
    setShowFinish(false);
  }, [isFinished]);

  /** Pokreni Lottie kad postane vidljiv */
  useEffect(() => {
    if (showFinish && lottieRef.current) {
      lottieRef.current.setSpeed(0.7);
      lottieRef.current.goToAndPlay(0);
    }
  }, [showFinish]);

  return (
    <div className="bg-blue-600 h-full w-full min-w-0 max-w-full overflow-hidden flex flex-col">
      <Logo
        variant="light"
        animated
        className="absolute top-8 left-8 z-10"
      />

      {/* Završna animacija — prikazuje se preko svega kad je vozilo objavljeno */}
      {isFinished && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center">
          <Lottie
            lottieRef={lottieRef}
            animationData={successAnimation}
            autoplay={false}
            loop={false}
            className={classNames(
              "w-[300px] h-auto transition-all duration-700 ease-out",
              showFinish ? "opacity-100 scale-100" : "opacity-0 scale-75"
            )}
          />
          <div
            className={classNames(
              "text-center transition-all duration-700 ease-out delay-500 max-w-md",
              showFinish ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            )}
          >
            <h1 className="text-4xl font-bold mb-3 text-white">
              Vozilo je uspješno kreirano!
            </h1>
            <p className="text-lg opacity-80 text-blue-50">
              Možete ga pregledati na stranici vozila ili kreirati novo.
            </p>
          </div>
        </div>
      )}

      {/* Normalni sadržaj — skriven kad je završna animacija aktivna */}
      {!isFinished && (
        <>
          <div className="relative flex-1 min-h-0">
            <VehicleIntroSummary
              vehicleInfo={vehicleInfo}
              stepCurrent={stepCurrent} />
            <VehiclePickerSummary
              vehicleInfo={vehicleInfo}
              stepCurrent={stepCurrent}
            />
          </div>

          <div className="mt-auto w-full min-w-0 max-w-full px-4">
            <div
              className="
                flex gap-4
                overflow-x-auto
                overflow-y-hidden
                snap-x snap-mandatory
                pb-4
                w-full
                min-w-0
                max-w-full
              "
            >
              <VehicleInfoSummary vehicleInfo={vehicleInfo} stepCurrent={stepCurrent} />
              <VehicleEquipmentSummary vehicleInfo={vehicleInfo} stepCurrent={stepCurrent} />
              <VehicleTireSummary vehicleInfo={vehicleInfo} stepCurrent={stepCurrent} />
              <VehicleFireExtinguisherSummary vehicleInfo={vehicleInfo} stepCurrent={stepCurrent} />
              <VehicleOwnerSummary vehicleInfo={vehicleInfo} stepCurrent={stepCurrent} />
              <VehiclePriceSummary vehicleInfo={vehicleInfo} stepCurrent={stepCurrent} />
              <VehiclePictureSummary vehicleInfo={vehicleInfo} stepCurrent={stepCurrent} />
              <VehicleDocumentationSummary vehicleInfo={vehicleInfo} stepCurrent={stepCurrent} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default memo(VehicleSummaryCard);
